/**
 * SubscriptionItemEditor - Simplified editor for a single subscription item
 *
 * Layout (labels above fields):
 * ┌─────────────────────────────────────────────────────────────┐
 * │ #N ≡ [Default Toggle]                              [Delete] │
 * ├─────────────────────────────────────────────────────────────┤
 * │ Product ID                                                   │
 * │ [_________________________________________________]         │
 * │                                                              │
 * │ Left Top              Right Top                              │
 * │ [_______________]     [_______________]                      │
 * │                                                              │
 * │ Left Bottom           Right Bottom                           │
 * │ [_______________]     [_______________]                      │
 * │                                                              │
 * │ ☐ Badge                                                     │
 * │ [_________________________________________________]         │
 * └─────────────────────────────────────────────────────────────┘
 *
 * Uses local state buffering (blur-to-save pattern) to prevent focus loss.
 *
 * Sprint: screen-editor-paywall
 * Task: Phase 2 - Subscriptions Section Refactoring
 */

import React, { useCallback, useState, useEffect, memo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Switch,
  Checkbox,
  Collapse,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { PaywallProduct } from '@segmently/schema-models/payments';
import { StyledTextField } from '@modules/ui-components';
import { PaywallVariableTextField } from '../../../shared/PaywallVariableTextField';
import { SPACING } from '../../../../config/ui';
import type { PaddlePickerConnectionProps } from '@modules/reactFlow/components/onboarding/editorSectionsV2/paywall/usePaddlePickerConnection';
import { PaywallProductPickerControl } from './PaywallProductPickerControl';

/**
 * Subscription item data structure (l10n values extracted for current locale)
 */
export interface SubscriptionItemData {
  id: string;              // For SortableList (e.g., "subscription-0")
  productId: string;       // Actual product ID (e.g., "com.app.subscription")
  leftLabelTop: string;
  leftLabelBottom: string;
  rightLabelTop: string;
  rightLabelBottom: string;
  descriptionLabel: string | null;
  purchaseLabel: string | null;
  badge: string | null;
  isDefault: boolean;
}

/**
 * Props for SubscriptionItemEditor
 */
export interface SubscriptionItemEditorProps extends PaddlePickerConnectionProps {
  /** Item index (0-based) */
  index: number;

  /** Current item data */
  item: SubscriptionItemData;

  /** Callback when any field changes */
  onChange: (field: keyof SubscriptionItemData, value: unknown) => void;

  /** Callback when delete button is clicked */
  onDelete?: () => void;

  /** Whether delete is allowed (false if minItems reached) */
  canDelete?: boolean;

  /** Whether editing is disabled */
  disabled?: boolean;

  // ── Picker props (forwarded to PaywallProductPickerControl) ──

  /**
   * Full PaywallProduct list — Stripe-only (Paddle filtered upstream).
   * The picker filters by mode (active tab) and search.
   */
  allProducts: PaywallProduct[];

  /** ProductIds of other items[k] for k !== this.index, for mode-mismatch detection (M5). */
  otherProductIds: string[];

  /** M7: whether the Sandbox Stripe account is connected. */
  sandboxConnected: boolean;

  /** M7: reason string for Sandbox not being connected (or null). */
  sandboxNotConnectedReason: string | null;

  /** M7: whether the Live Stripe account is connected. */
  liveConnected: boolean;

  /** M7: reason string for Live not being connected (or null). */
  liveNotConnectedReason: string | null;

  /** `usePaywallProducts.loading` */
  productsLoading: boolean;
  stripeConnectionLoading?: boolean;

  /** `usePaywallProducts.error` */
  productsError: string | null;

  /** `usePaywallProducts.refresh` */
  onRetryProducts: () => Promise<void>;

  /** `usePaywallProducts.createProduct` — picker calls this from inline create flow (M4). */
  onCreateProduct: (
    data: Omit<PaywallProduct, 'id' | 'createdAt' | 'updatedAt'>,
  ) => Promise<PaywallProduct>;

  /** M7: optional save handler threaded from V2 editor dialog. */
  onSaveScreen?: () => Promise<void>;

  /** M7: whether the editor has unsaved changes (drives save-guard modal). */
  hasUnsavedChanges?: boolean;

  /** M7: step ID of the screen being edited; stashed in sessionStorage on OAuth redirect. */
  stepId?: string;

  /** Forwarded to picker → PaywallProductDialog. */
  locale: string;

  /** Forwarded to picker → PaywallProductDialog. */
  projectId: string;

  /** Stable key for preserving the inline picker state across parent remounts. */
  pickerPersistenceKey?: string;
}

/**
 * SubscriptionItemEditor - Simplified card-like editor for a single subscription
 */
const SubscriptionItemEditorComponent: React.FC<SubscriptionItemEditorProps> = ({
  index,
  item,
  onChange,
  onDelete,
  canDelete = true,
  disabled = false,
  allProducts,
  otherProductIds,
  paddleSandboxConnected,
  paddleProductionConnected,
  paddleSandboxNotConnectedReason,
  paddleProductionNotConnectedReason,
  sandboxConnected,
  sandboxNotConnectedReason,
  liveConnected,
  liveNotConnectedReason,
  productsLoading,
  stripeConnectionLoading,
  productsError,
  onRetryProducts,
  onCreateProduct,
  onSaveScreen,
  hasUnsavedChanges,
  stepId,
  locale,
  projectId,
  pickerPersistenceKey,
}) => {
  // Local state for text fields - prevents focus loss on parent re-render
  // Note: productId is now handled via Select, no local state needed
  const [localLeftTop, setLocalLeftTop] = useState(item.leftLabelTop);
  const [localLeftBottom, setLocalLeftBottom] = useState(item.leftLabelBottom);
  const [localRightTop, setLocalRightTop] = useState(item.rightLabelTop);
  const [localRightBottom, setLocalRightBottom] = useState(item.rightLabelBottom);
  const [localDescription, setLocalDescription] = useState(item.descriptionLabel || '');
  const [localPurchaseLabel, setLocalPurchaseLabel] = useState(item.purchaseLabel || '');
  const [localBadge, setLocalBadge] = useState(item.badge || '');
  const [descriptionEnabled, setDescriptionEnabled] = useState(Boolean(item.descriptionLabel));
  const [purchaseLabelEnabled, setPurchaseLabelEnabled] = useState(Boolean(item.purchaseLabel));
  const [badgeEnabled, setBadgeEnabled] = useState(Boolean(item.badge));

  // Sync props to local state when external changes occur (e.g., reorder, undo)
  // Consolidated into single effect to prevent race conditions and reduce re-renders
  // React batches all state updates within the same effect
  useEffect(() => {
    setLocalLeftTop(item.leftLabelTop);
    setLocalLeftBottom(item.leftLabelBottom);
    setLocalRightTop(item.rightLabelTop);
    setLocalRightBottom(item.rightLabelBottom);
    setLocalDescription(item.descriptionLabel || '');
    setLocalPurchaseLabel(item.purchaseLabel || '');
    setLocalBadge(item.badge || '');
    setDescriptionEnabled(Boolean(item.descriptionLabel));
    setPurchaseLabelEnabled(Boolean(item.purchaseLabel));
    setBadgeEnabled(Boolean(item.badge));
  }, [
    item.leftLabelTop,
    item.leftLabelBottom,
    item.rightLabelTop,
    item.rightLabelBottom,
    item.descriptionLabel,
    item.purchaseLabel,
    item.badge,
  ]);

  // Picker emits the chosen productId directly (no MUI event wrapper).
  const handlePickerSelect = useCallback(
    (productId: string) => {
      onChange('productId', productId);
    },
    [onChange]
  );

  // Blur handlers for text fields
  const handleLeftTopBlur = useCallback(() => {
    if (localLeftTop !== item.leftLabelTop) {
      onChange('leftLabelTop', localLeftTop);
    }
  }, [localLeftTop, item.leftLabelTop, onChange]);

  const handleLeftBottomBlur = useCallback(() => {
    if (localLeftBottom !== item.leftLabelBottom) {
      onChange('leftLabelBottom', localLeftBottom);
    }
  }, [localLeftBottom, item.leftLabelBottom, onChange]);

  const handleRightTopBlur = useCallback(() => {
    if (localRightTop !== item.rightLabelTop) {
      onChange('rightLabelTop', localRightTop);
    }
  }, [localRightTop, item.rightLabelTop, onChange]);

  const handleRightBottomBlur = useCallback(() => {
    if (localRightBottom !== item.rightLabelBottom) {
      onChange('rightLabelBottom', localRightBottom);
    }
  }, [localRightBottom, item.rightLabelBottom, onChange]);

  const handleDescriptionBlur = useCallback(() => {
    if (descriptionEnabled) {
      const newValue = localDescription.trim() || null;
      if (newValue !== item.descriptionLabel) {
        onChange('descriptionLabel', newValue);
      }
    }
  }, [descriptionEnabled, item.descriptionLabel, localDescription, onChange]);

  const handlePurchaseLabelBlur = useCallback(() => {
    if (purchaseLabelEnabled) {
      const newValue = localPurchaseLabel.trim() || null;
      if (newValue !== item.purchaseLabel) {
        onChange('purchaseLabel', newValue);
      }
    }
  }, [item.purchaseLabel, localPurchaseLabel, onChange, purchaseLabelEnabled]);

  const handleBadgeBlur = useCallback(() => {
    if (badgeEnabled) {
      const newValue = localBadge.trim() || null;
      if (newValue !== item.badge) {
        onChange('badge', newValue);
      }
    }
  }, [localBadge, item.badge, badgeEnabled, onChange]);

  // Toggle handlers
  const handleDefaultToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange('isDefault', event.target.checked);
    },
    [onChange]
  );

  const handleDescriptionToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const enabled = event.target.checked;
      setDescriptionEnabled(enabled);
      if (!enabled) {
        onChange('descriptionLabel', null);
        setLocalDescription('');
      }
    },
    [onChange]
  );

  const handlePurchaseLabelToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const enabled = event.target.checked;
      setPurchaseLabelEnabled(enabled);
      if (!enabled) {
        onChange('purchaseLabel', null);
        setLocalPurchaseLabel('');
      }
    },
    [onChange]
  );

  // Badge checkbox handler
  const handleBadgeToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const enabled = event.target.checked;
      setBadgeEnabled(enabled);
      if (!enabled) {
        // Clear badge when disabled
        onChange('badge', null);
        setLocalBadge('');
      }
    },
    [onChange]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        pt: 0.5,
        px: 1.5,
        pb: 1.5,
        bgcolor: 'background.paper',
        borderRadius: SPACING.sm,
        border: '1px solid',
        borderColor: item.isDefault ? 'primary.main' : 'divider',
        '&:hover': {
          borderColor: item.isDefault ? 'primary.main' : 'action.hover',
        },
      }}
      data-testid={`subscription-item-editor-${index}`}
    >
      {/* Header row: # + Default toggle + Delete */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Left side: # and Default toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.disabled" sx={{ width: 24, flexShrink: 0 }}>
            #{index + 1}
          </Typography>
          {/* Spacer for drag handle */}
          <Box sx={{ width: 24, flexShrink: 0 }} />
          <Tooltip title="Pre-selected subscription">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Switch
                checked={item.isDefault}
                onChange={handleDefaultToggle}
                disabled={disabled}
                size="small"
                data-testid={`subscription-item-${index}-default`}
              />
              <Typography variant="caption" color={item.isDefault ? 'primary.main' : 'text.secondary'}>
                Default
              </Typography>
            </Box>
          </Tooltip>
        </Box>

        {/* Right side: delete button */}
        {onDelete && (
          <Tooltip title={canDelete ? 'Delete item' : 'Minimum items reached'}>
            <span>
              <IconButton
                size="small"
                onClick={onDelete}
                disabled={disabled || !canDelete}
                data-testid={`subscription-item-${index}-delete`}
                sx={{
                  opacity: 0.5,
                  '&:hover': {
                    opacity: 1,
                    color: 'error.main',
                  },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* Product field — inline expandable picker (M1 skeleton; M2-M5 fill panel). */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Product
        </Typography>
        <PaywallProductPickerControl
          rowIndex={index}
          currentProductId={item.productId}
          allProducts={allProducts}
          otherProductIds={otherProductIds}
          paddleSandboxConnected={paddleSandboxConnected}
          paddleProductionConnected={paddleProductionConnected}
          paddleSandboxNotConnectedReason={paddleSandboxNotConnectedReason}
          paddleProductionNotConnectedReason={paddleProductionNotConnectedReason}
          sandboxConnected={sandboxConnected}
          sandboxNotConnectedReason={sandboxNotConnectedReason}
          liveConnected={liveConnected}
          liveNotConnectedReason={liveNotConnectedReason}
          productsLoading={productsLoading}
          stripeConnectionLoading={stripeConnectionLoading}
          productsError={productsError}
          onRetryProducts={onRetryProducts}
          onSelect={handlePickerSelect}
          onCreateProduct={onCreateProduct}
          onSaveScreen={onSaveScreen}
          hasUnsavedChanges={hasUnsavedChanges}
          stepId={stepId}
          locale={locale}
          projectId={projectId}
          persistenceKey={pickerPersistenceKey}
          disabled={disabled}
        />
      </Box>

      {/* Label fields - 2x2 grid with labels above */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1.5,
        }}
      >
        {/* Left Top - with paywall variable support */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Left Top
          </Typography>
          <PaywallVariableTextField
            value={localLeftTop}
            onChange={setLocalLeftTop}
            onBlur={handleLeftTopBlur}
            disabled={disabled}
            size="small"
            fullWidth
            placeholder="@duration"
            multiline
            minRows={1}
            maxRows={3}
            data-testid={`subscription-item-${index}-leftLabelTop`}
          />
        </Box>

        {/* Right Top - with paywall variable support */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Right Top
          </Typography>
          <PaywallVariableTextField
            value={localRightTop}
            onChange={setLocalRightTop}
            onBlur={handleRightTopBlur}
            disabled={disabled}
            size="small"
            fullWidth
            placeholder="@price"
            multiline
            minRows={1}
            maxRows={3}
            data-testid={`subscription-item-${index}-rightLabelTop`}
          />
        </Box>

        {/* Left Bottom - with paywall variable support */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Left Bottom
          </Typography>
          <PaywallVariableTextField
            value={localLeftBottom}
            onChange={setLocalLeftBottom}
            onBlur={handleLeftBottomBlur}
            disabled={disabled}
            size="small"
            fullWidth
            placeholder="@trialDays"
            multiline
            minRows={1}
            maxRows={3}
            data-testid={`subscription-item-${index}-leftLabelBottom`}
          />
        </Box>

        {/* Right Bottom - with paywall variable support */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Right Bottom
          </Typography>
          <PaywallVariableTextField
            value={localRightBottom}
            onChange={setLocalRightBottom}
            onBlur={handleRightBottomBlur}
            disabled={disabled}
            size="small"
            fullWidth
            placeholder="@fullTrialDays"
            multiline
            minRows={1}
            maxRows={3}
            data-testid={`subscription-item-${index}-rightLabelBottom`}
          />
        </Box>
      </Box>

      {/* Description label */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Checkbox
            checked={descriptionEnabled}
            onChange={handleDescriptionToggle}
            disabled={disabled}
            size="small"
            sx={{ p: 0 }}
            data-testid={`subscription-item-${index}-description-toggle`}
          />
          <Typography
            variant="caption"
            color={descriptionEnabled ? 'text.secondary' : 'text.disabled'}
          >
            Description
          </Typography>
        </Box>
        <Collapse in={descriptionEnabled} timeout="auto" unmountOnExit>
          <PaywallVariableTextField
            value={localDescription}
            onChange={setLocalDescription}
            onBlur={handleDescriptionBlur}
            disabled={disabled || !descriptionEnabled}
            size="small"
            fullWidth
            placeholder="Description label"
            multiline
            minRows={1}
            maxRows={3}
            data-testid={`subscription-item-${index}-descriptionLabel`}
          />
        </Collapse>
      </Box>

      {/* Purchase label */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Checkbox
            checked={purchaseLabelEnabled}
            onChange={handlePurchaseLabelToggle}
            disabled={disabled}
            size="small"
            sx={{ p: 0 }}
            data-testid={`subscription-item-${index}-purchase-toggle`}
          />
          <Typography
            variant="caption"
            color={purchaseLabelEnabled ? 'text.secondary' : 'text.disabled'}
          >
            Purchase label
          </Typography>
        </Box>
        <Collapse in={purchaseLabelEnabled} timeout="auto" unmountOnExit>
          <PaywallVariableTextField
            value={localPurchaseLabel}
            onChange={setLocalPurchaseLabel}
            onBlur={handlePurchaseLabelBlur}
            disabled={disabled || !purchaseLabelEnabled}
            size="small"
            fullWidth
            placeholder="Purchase label"
            multiline
            minRows={1}
            maxRows={3}
            data-testid={`subscription-item-${index}-purchaseLabel`}
          />
        </Collapse>
      </Box>

      {/* Badge field - optional with checkbox */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Checkbox
            checked={badgeEnabled}
            onChange={handleBadgeToggle}
            disabled={disabled}
            size="small"
            sx={{ p: 0 }}
            data-testid={`subscription-item-${index}-badge-toggle`}
          />
          <Typography
            variant="caption"
            color={badgeEnabled ? 'text.secondary' : 'text.disabled'}
          >
            Badge
          </Typography>
        </Box>
        <Collapse in={badgeEnabled} timeout="auto" unmountOnExit>
          <StyledTextField
            value={localBadge}
            onChange={setLocalBadge}
            onBlur={handleBadgeBlur}
            disabled={disabled || !badgeEnabled}
            size="small"
            fullWidth
            placeholder="POPULAR"
            data-testid={`subscription-item-${index}-badge`}
          />
        </Collapse>
      </Box>
    </Box>
  );
};

// Wrap in React.memo to prevent re-renders when parent struct changes
export const SubscriptionItemEditor = memo(SubscriptionItemEditorComponent);

export default SubscriptionItemEditor;
